import React from 'react';
import { getMentorAppStatus, canReapply, reapplyUnlockDate } from '../utils/mentorApp';

/**
 * MentorApplicationStatus
 * Shows a compact status card for mentors whose application is pending or rejected.
 * Returns null if no status exists or the mentor is already approved.
 *
 * Props:
 *   userId   — the authenticated user's id
 *   onReapply — callback when the "Reapply" button is clicked
 */
export default function MentorApplicationStatus({ userId, onReapply }) {
  if (!userId) return null;

  const app = getMentorAppStatus(userId);
  if (!app || app.status === 'approved') return null;

  const submittedDate = app.submittedAt
    ? new Date(app.submittedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  if (app.status === 'pending') {
    return (
      <div style={{
        background: '#fffbeb',
        border: '1.5px solid #fde68a',
        borderRadius: 14,
        padding: '16px 20px',
        display: 'flex',
        gap: 14,
        alignItems: 'flex-start',
      }}>
        <div style={{ fontSize: '1.5rem', lineHeight: 1, flexShrink: 0 }}>⏳</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: '.9375rem', color: '#92400e', marginBottom: 4 }}>
            Application Under Review
          </div>
          <div style={{ fontSize: '.84rem', color: '#b45309', lineHeight: 1.6 }}>
            Your application is under review. You cannot apply again while it is being reviewed.
            Our team will respond within 24–48 hours.
          </div>
          {submittedDate && (
            <div style={{ fontSize: '.75rem', color: '#d97706', marginTop: 6, fontWeight: 600 }}>
              Submitted on {submittedDate}
            </div>
          )}
        </div>
        <div style={{
          background: '#fef3c7',
          border: '1px solid #fde68a',
          borderRadius: 999,
          padding: '4px 12px',
          fontSize: '.72rem',
          fontWeight: 800,
          color: '#92400e',
          flexShrink: 0,
          alignSelf: 'center',
        }}>
          PENDING
        </div>
      </div>
    );
  }

  if (app.status === 'rejected') {
    const eligible = canReapply(app);
    const unlockDate = reapplyUnlockDate(app);
    const daysLeft = Math.ceil(
      ((app.rejectedAt || 0) + 30 * 24 * 60 * 60 * 1000 - Date.now()) / (24 * 60 * 60 * 1000)
    );

    if (!eligible) {
      return (
        <div style={{
          background: '#fef2f2',
          border: '1.5px solid #fecaca',
          borderRadius: 14,
          padding: '16px 20px',
          display: 'flex',
          gap: 14,
          alignItems: 'flex-start',
        }}>
          <div style={{ fontSize: '1.5rem', lineHeight: 1, flexShrink: 0 }}>❌</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: '.9375rem', color: '#991b1b', marginBottom: 4 }}>
              Application Not Approved
            </div>
            <div style={{ fontSize: '.84rem', color: '#b91c1c', lineHeight: 1.6 }}>
              Your application was rejected. You can reapply on{' '}
              <strong>{unlockDate}</strong>.
            </div>
            <div style={{ fontSize: '.75rem', color: '#dc2626', marginTop: 6, fontWeight: 600 }}>
              {daysLeft > 0 ? `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining before you can reapply` : 'Reapply available soon'}
            </div>
          </div>
          <div style={{
            background: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: 999,
            padding: '4px 12px',
            fontSize: '.72rem',
            fontWeight: 800,
            color: '#991b1b',
            flexShrink: 0,
            alignSelf: 'center',
          }}>
            REJECTED
          </div>
        </div>
      );
    }

    // Eligible to reapply
    return (
      <div style={{
        background: '#fff7ed',
        border: '1.5px solid #fed7aa',
        borderRadius: 14,
        padding: '16px 20px',
        display: 'flex',
        gap: 14,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: '1.5rem', lineHeight: 1, flexShrink: 0 }}>🔄</div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontWeight: 800, fontSize: '.9375rem', color: '#9a3412', marginBottom: 4 }}>
            Ready to Reapply
          </div>
          <div style={{ fontSize: '.84rem', color: '#c2410c', lineHeight: 1.6 }}>
            30 days have passed since your rejection. You can now submit a new application.
          </div>
        </div>
        <button
          onClick={onReapply}
          style={{
            background: 'linear-gradient(135deg, #f97316, #ea580c)',
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            padding: '10px 20px',
            fontWeight: 800,
            fontSize: '.84rem',
            cursor: 'pointer',
            flexShrink: 0,
            boxShadow: '0 4px 12px rgba(249, 115, 22, 0.35)',
          }}
        >
          Reapply Now
        </button>
      </div>
    );
  }

  return null;
}
